/**
 * The firm's phones and tablets — which is in a car and which is in the drawer — and the
 * drivers' own phones beside them.
 *
 * A firm with twenty phones and twenty tablets hands them out at the start of a shift and
 * takes them back at the end, and this is the one place that says where each one is. Every
 * device the firm has registered stays here — given to a driver, taken back, given to the
 * next — until the office writes it off as broken, stolen or lost, and even then it is kept,
 * behind its own filter, for the record.
 *
 * Registered here and only here, with a QR code for nobody yet; given from here without a
 * code, because the device is already the firm's and only waits to be told whose it is. A
 * driver's own phone, registered from his own dialog or his request, is listed too, so the
 * office sees every device that signs somebody in — but it is his alone: never given to
 * anybody else, never written off, and forgotten when taken back. Every action stops whoever had the device at once — the server makes sure
 * of that, not this screen. One nobody has may also be deleted: forgotten, history and all, so
 * that the device registers afresh as a stranger would.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { devicesApi, type DeviceKind, type DeviceRetirement, type FirmDevice } from '../../devices/api';
import { QrTicket } from '../../devices/QrTicket';
import { deviceTitle } from '../../devices/words';
import { useTenantFormats } from '../../shell/formats';
import {
  Banner,
  Button,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  PageHeader,
  Pill,
  Section,
  Segmented,
  Select,
  Sub,
  Table,
  Td,
  Th,
  Tr,
} from '../../ui';
import { describe } from '../errors';
import { fleetApi, type FleetDriver } from '../fleet/api';

type KindFilter = 'all' | DeviceKind;
type OwnerFilter = 'all' | 'firm' | 'personal';
type StatusFilter = 'all' | 'inUse' | 'free' | 'retired';

/** How often the list asks again, so a device given from another desk shows up here too. */
const LOOK_EVERY_MS = 15_000;

const REASONS: DeviceRetirement[] = ['Broken', 'Stolen', 'Lost'];

export function DevicesPage() {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const [devices, setDevices] = useState<FirmDevice[] | null>(null);
  const [drivers, setDrivers] = useState<FleetDriver[]>([]);
  const [kind, setKind] = useState<KindFilter>('all');
  const [owner, setOwner] = useState<OwnerFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // One dialog at a time, each holding the device it is about.
  const [registering, setRegistering] = useState(false);
  const [giving, setGiving] = useState<FirmDevice | null>(null);
  const [editing, setEditing] = useState<FirmDevice | null>(null);
  const [releasing, setReleasing] = useState<FirmDevice | null>(null);
  const [retiring, setRetiring] = useState<FirmDevice | null>(null);
  const [deleting, setDeleting] = useState<FirmDevice | null>(null);

  const load = useCallback(async () => {
    try {
      setDevices(await devicesApi.list());
    } catch (failure) {
      setError(describe(failure, t, 'devices.couldNotLoad'));
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void load();

    const timer = setInterval(() => void load(), LOOK_EVERY_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    void fleetApi
      .drivers()
      .then((roster) => setDrivers(roster.filter((one) => one.isActive)))
      .catch(() => setDrivers([]));
  }, []);

  async function act(action: () => Promise<void>) {
    setBusy(true);
    setError(null);

    try {
      await action();
      await load();
      return true;
    } catch (failure) {
      setError(describe(failure, t, 'devices.couldNotSave'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const live = useMemo(() => (devices ?? []).filter((one) => one.retiredAt === null), [devices]);

  // Counted within the kind and the owner chosen, so "Free · 8" is eight of the tablets when
  // tablets are shown.
  const ofKind = (list: FirmDevice[]) =>
    list.filter(
      (one) =>
        (kind === 'all' || one.kind === kind) &&
        (owner === 'all' || one.personal === (owner === 'personal')),
    );

  const counts = {
    all: ofKind(live).length,
    inUse: ofKind(live).filter((one) => one.driver !== null).length,
    free: ofKind(live).filter((one) => one.driver === null).length,
    retired: ofKind((devices ?? []).filter((one) => one.retiredAt !== null)).length,
  };

  const shown = ofKind(
    status === 'retired'
      ? (devices ?? []).filter((one) => one.retiredAt !== null)
      : live.filter((one) =>
          status === 'inUse' ? one.driver !== null : status === 'free' ? one.driver === null : true,
        ),
  );

  return (
    <>
      <PageHeader title={t('nav.settings.devices')} description={t('nav.settingsAbout.devices')} />

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <Section
        title={t('devices.list.title')}
        description={t('devices.list.help')}
        actions={
          <Button size="sm" disabled={busy} onClick={() => setRegistering(true)}>
            <Icon name="plus" small />
            {t('devices.list.register')}
          </Button>
        }
      >
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <Segmented
            label={t('devices.list.kindFilter')}
            value={kind}
            options={[
              { value: 'all', label: t('devices.list.allKinds') },
              { value: 'Phone', label: t('devices.list.phones') },
              { value: 'Tablet', label: t('devices.list.tablets') },
            ]}
            onChange={setKind}
          />
          <Segmented
            label={t('devices.list.ownerFilter')}
            value={owner}
            options={[
              { value: 'all', label: t('devices.list.allOwners') },
              { value: 'firm', label: t('devices.list.firms') },
              { value: 'personal', label: t('devices.list.personals') },
            ]}
            onChange={setOwner}
          />
          <Segmented
            label={t('devices.list.statusFilter')}
            value={status}
            options={[
              { value: 'all', label: `${t('devices.list.all')} · ${formats.count(counts.all)}` },
              { value: 'inUse', label: `${t('devices.list.inUse')} · ${formats.count(counts.inUse)}` },
              { value: 'free', label: `${t('devices.list.free')} · ${formats.count(counts.free)}` },
              { value: 'retired', label: `${t('devices.list.retired')} · ${formats.count(counts.retired)}` },
            ]}
            onChange={setStatus}
          />
        </div>

        {devices === null ? (
          <EmptyState>{t('app.loading')}</EmptyState>
        ) : shown.length === 0 ? (
          <EmptyState>{t(devices.length === 0 ? 'devices.list.none' : 'devices.list.noneHere')}</EmptyState>
        ) : (
          <Table>
            <thead>
              <Tr>
                <Th>{t('devices.label')}</Th>
                <Th>{t('devices.list.kind')}</Th>
                <Th>{t('devices.list.model')}</Th>
                <Th>{t('devices.list.state')}</Th>
                <Th>{t('devices.list.lastUsed')}</Th>
                <Th />
              </Tr>
            </thead>
            <tbody>
              {shown.map((device) => (
                <Tr key={device.id} className={device.retiredAt === null ? undefined : 'text-ink-3'}>
                  <Td>
                    <span className="font-semibold">{device.label ?? '—'}</span>
                  </Td>
                  {/* Whose under what, in one column: the row has five buttons to fit. */}
                  <Td>
                    {t(`devices.kind.${device.kind}`)}
                    <Sub block>{t(device.personal ? 'devices.owner.personal' : 'devices.owner.firm')}</Sub>
                  </Td>
                  <Td wrap>{device.name ?? t('devices.unnamed')}</Td>
                  <Td wrap>
                    <State device={device} />
                  </Td>
                  <Td>
                    {device.lastSessionAt === null ? (
                      <Sub>{t('devices.list.never')}</Sub>
                    ) : (
                      formats.moment(device.lastSessionAt)
                    )}
                  </Td>
                  <Td>
                    {device.personal ? (
                      // His alone: changed, or taken back — which forgets it. Nothing else.
                      <span className="flex flex-wrap justify-end gap-1.5">
                        <Button size="sm" disabled={busy} onClick={() => setEditing(device)}>
                          {t('fleet.edit')}
                        </Button>
                        <Button size="sm" disabled={busy} onClick={() => setReleasing(device)}>
                          {t('devices.dialog.remove')}
                        </Button>
                      </span>
                    ) : device.retiredAt === null ? (
                      <span className="flex flex-wrap justify-end gap-1.5">
                        {device.driver === null ? (
                          <Button size="sm" disabled={busy} onClick={() => setGiving(device)}>
                            {t('devices.list.give')}
                          </Button>
                        ) : (
                          <Button size="sm" disabled={busy} onClick={() => setReleasing(device)}>
                            {t('devices.dialog.release')}
                          </Button>
                        )}
                        <Button size="sm" disabled={busy} onClick={() => setEditing(device)}>
                          {t('fleet.edit')}
                        </Button>
                        <Button size="sm" disabled={busy} onClick={() => setRetiring(device)}>
                          {t('devices.list.retire')}
                        </Button>
                        {device.driver === null && (
                          <Button size="sm" disabled={busy} onClick={() => setDeleting(device)}>
                            {t('devices.list.delete')}
                          </Button>
                        )}
                      </span>
                    ) : (
                      <span className="flex justify-end">
                        <Button size="sm" disabled={busy} onClick={() => setDeleting(device)}>
                          {t('devices.list.delete')}
                        </Button>
                      </span>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      {registering && (
        <RegisterDialog
          onScanned={() => void load()}
          onClose={() => {
            setRegistering(false);
            void load();
          }}
        />
      )}

      {giving !== null && (
        <GiveDialog
          device={giving}
          drivers={drivers}
          busy={busy}
          error={error}
          onGive={(driverId) =>
            void act(() => devicesApi.assign(giving.id, driverId)).then((done) => done && setGiving(null))
          }
          onClose={() => setGiving(null)}
        />
      )}

      {editing !== null && (
        <EditDialog
          device={editing}
          busy={busy}
          error={error}
          onSave={(label, kindOf) =>
            void act(() => devicesApi.update(editing.id, { label, kind: kindOf })).then(
              (done) => done && setEditing(null),
            )
          }
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={releasing !== null}
        title={t(releasing?.personal ? 'devices.dialog.confirmRemoveTitle' : 'devices.dialog.confirmReleaseTitle')}
        confirm={t(releasing?.personal ? 'devices.dialog.remove' : 'devices.dialog.release')}
        cancel={t('devices.dialog.keep')}
        onConfirm={() => {
          const device = releasing;
          setReleasing(null);
          if (device !== null) void act(() => devicesApi.release(device.id));
        }}
        onClose={() => setReleasing(null)}
      >
        {releasing !== null &&
          t(releasing.personal ? 'devices.dialog.confirmRemove' : 'devices.dialog.confirmRelease', {
            device: deviceTitle(t, releasing),
            name: releasing.driver?.displayName ?? '',
          })}
      </ConfirmDialog>

      {/* Only for one nobody has: a device in a driver's hands is taken back first, by the
          button that says it signs him out. */}
      <ConfirmDialog
        open={deleting !== null}
        title={t('devices.list.deleteTitle')}
        confirm={t('devices.list.delete')}
        cancel={t('devices.dialog.keep')}
        onConfirm={() => {
          const device = deleting;
          setDeleting(null);
          if (device !== null) void act(() => devicesApi.remove(device.id));
        }}
        onClose={() => setDeleting(null)}
      >
        {deleting !== null && t('devices.list.deleteHelp', { device: deviceTitle(t, deleting) })}
      </ConfirmDialog>

      {retiring !== null && (
        <RetireDialog
          device={retiring}
          busy={busy}
          error={error}
          onRetire={(reason) =>
            void act(() => devicesApi.retire(retiring.id, reason)).then((done) => done && setRetiring(null))
          }
          onClose={() => setRetiring(null)}
        />
      )}
    </>
  );
}

/** In use and by whom, and whether they are working; free; or written off and why. */
function State({ device }: { device: FirmDevice }) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  if (device.retiredAt !== null) {
    return (
      <>
        <Pill tone="danger" soft>
          {t(`devices.retirement.${device.retiredReason ?? 'Broken'}`)}
        </Pill>
        <Sub block>
          {t('devices.list.retiredOn', { date: formats.moment(device.retiredAt) })}
          {device.driver !== null && ` · ${t('devices.list.lastWith', { name: device.driver.displayName })}`}
        </Sub>
      </>
    );
  }

  if (device.driver === null) {
    return <Pill>{t('devices.list.freeOne')}</Pill>;
  }

  const working = device.driver.shiftState !== 'OffDuty';

  return (
    <>
      <span className="flex items-center gap-1.5">
        <Pill tone={working ? 'ok' : 'info'} soft>
          {t('devices.list.inUse')}
        </Pill>
        {device.driver.callSign === null
          ? device.driver.displayName
          : `${device.driver.callSign} · ${device.driver.displayName}`}
      </span>
      <Sub block>{t(`shiftStates.${device.driver.shiftState}`, { defaultValue: device.driver.shiftState })}</Sub>
    </>
  );
}

/** A QR code for a device nobody has yet, with the number the office gives it first. */
function RegisterDialog({ onScanned, onClose }: { onScanned: () => void; onClose: () => void }) {
  const { t } = useTranslation();

  const [label, setLabel] = useState('');
  const [showing, setShowing] = useState(false);

  return (
    <Dialog
      open
      title={t('devices.list.registerTitle')}
      onClose={onClose}
      actions={
        showing ? (
          <Button variant="quiet" onClick={onClose}>
            {t('devices.close')}
          </Button>
        ) : (
          <>
            <Button variant="quiet" onClick={onClose}>
              {t('fleet.cancel')}
            </Button>
            <Button variant="primary" onClick={() => setShowing(true)}>
              {t('devices.list.showQr')}
            </Button>
          </>
        )
      }
    >
      {showing ? (
        <QrTicket target={{ label: label.trim() === '' ? null : label.trim() }} onScanned={onScanned} />
      ) : (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-secondary text-ink-3">{t('devices.list.registerHelp')}</p>
          <Field id="label" label={t('devices.label')} help={t('devices.labelHelp')}>
            <Input value={label} maxLength={20} autoFocus onChange={(e) => setLabel(e.target.value)} />
          </Field>
        </div>
      )}
    </Dialog>
  );
}

function GiveDialog({
  device,
  drivers,
  busy,
  error,
  onGive,
  onClose,
}: {
  device: FirmDevice;
  drivers: FleetDriver[];
  busy: boolean;
  error: string | null;
  onGive: (driverId: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [driverId, setDriverId] = useState('');

  return (
    <Dialog
      open
      title={t('devices.list.giveTitle', { device: deviceTitle(t, device) })}
      onClose={onClose}
      actions={
        <>
          <Button variant="quiet" onClick={onClose}>
            {t('fleet.cancel')}
          </Button>
          <Button variant="primary" disabled={busy || driverId === ''} onClick={() => onGive(driverId)}>
            {t('devices.dialog.give')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error !== null && <Banner tone="danger">{error}</Banner>}
        <Field id="driverId" label={t('devices.approve.driver')} help={t('devices.list.giveHelp')}>
          <Select value={driverId} onChange={(e) => setDriverId(e.target.value)}>
            <option value="">{t('devices.approve.pickDriver')}</option>
            {drivers.map((one) => (
              <option key={one.id} value={one.id}>
                {one.callSign === null ? one.displayName : `${one.callSign} · ${one.displayName}`}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}

function EditDialog({
  device,
  busy,
  error,
  onSave,
  onClose,
}: {
  device: FirmDevice;
  busy: boolean;
  error: string | null;
  onSave: (label: string | null, kind: DeviceKind) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const [label, setLabel] = useState(device.label ?? '');
  const [kind, setKind] = useState<DeviceKind>(device.kind === 'Tablet' ? 'Tablet' : 'Phone');

  return (
    <Dialog
      open
      title={t('devices.list.editTitle', { device: deviceTitle(t, device) })}
      onClose={onClose}
      actions={
        <>
          <Button variant="quiet" onClick={onClose}>
            {t('fleet.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => onSave(label.trim() === '' ? null : label.trim(), kind)}
          >
            {t('fleet.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error !== null && <Banner tone="danger">{error}</Banner>}
        <Field id="label" label={t('devices.label')} help={t('devices.labelHelp')}>
          <Input value={label} maxLength={20} autoFocus onChange={(e) => setLabel(e.target.value)} />
        </Field>
        <Field id="kind" label={t('devices.list.kind')} help={t('devices.list.kindHelp')}>
          <Segmented
            label={t('devices.list.kind')}
            value={kind}
            options={[
              { value: 'Phone', label: t('devices.kind.Phone') },
              { value: 'Tablet', label: t('devices.kind.Tablet') },
            ]}
            onChange={setKind}
          />
        </Field>
      </div>
    </Dialog>
  );
}

/** Why it is being written off, asked first: it stops at once and never signs in again. */
function RetireDialog({
  device,
  busy,
  error,
  onRetire,
  onClose,
}: {
  device: FirmDevice;
  busy: boolean;
  error: string | null;
  onRetire: (reason: DeviceRetirement) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<DeviceRetirement>('Broken');

  return (
    <Dialog
      open
      title={t('devices.list.retireTitle', { device: deviceTitle(t, device) })}
      onClose={onClose}
      actions={
        <>
          <Button variant="quiet" onClick={onClose}>
            {t('devices.dialog.keep')}
          </Button>
          <Button variant="danger" disabled={busy} onClick={() => onRetire(reason)}>
            {t('devices.list.retire')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error !== null && <Banner tone="danger">{error}</Banner>}
        <Segmented
          label={t('devices.list.why')}
          value={reason}
          options={REASONS.map((one) => ({ value: one, label: t(`devices.retirement.${one}`) }))}
          onChange={setReason}
        />
        <p className="m-0 leading-[1.45]">
          {device.driver === null
            ? t('devices.list.retireHelp')
            : t('devices.list.retireHelpInUse', { name: device.driver.displayName })}
        </p>
      </div>
    </Dialog>
  );
}
